export type CreditUiVariant = "dark" | "light";

export const getCreditUi = (variant: CreditUiVariant) => {
    if (variant === "light") {
        return {
            page: "w-full text-[#4A4A48]",
            heading: "text-2xl sm:text-3xl font-bold text-[#4A4A48]",
            subtitle: "text-[#8A8A85] mt-2 text-sm sm:text-base",
            chip: "inline-flex items-center gap-1.5 bg-[#eef9fc] border border-[#D4EBEF] text-[#2fa9bf] px-3 py-1 rounded-full text-xs",
            card: "bg-white border border-[#E8E8E4] rounded-2xl shadow-sm",
            cardPopular:
                "bg-white border border-[#2fa9bf]/40 rounded-2xl shadow-sm ring-1 ring-[#2fa9bf]/15",
            muted: "text-[#8A8A85]",
            body: "text-[#4A4A48]",
            accent: "text-[#2fa9bf]",
            success: "text-emerald-600",
            warning: "text-amber-600",
            danger: "text-red-600",
            primaryBtn:
                "bg-[#2fa9bf] hover:bg-[#2296aa] text-white font-semibold rounded-xl transition",
            secondaryBtn:
                "border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] font-semibold rounded-xl transition",
            input: "w-full rounded-xl border border-[#DDDDD8] bg-[#F8F8F6] px-4 py-3 text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20",
            select: "bg-white border border-[#DDDDD8] rounded-xl py-3 px-4 text-[#4A4A48] focus:outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20",
            tableWrap:
                "bg-white border border-[#E8E8E4] rounded-2xl overflow-hidden shadow-sm",
            tableHead: "bg-[#F8F8F6] border-b border-[#E8E8E4]",
            tableHeadCell:
                "px-6 py-4 text-left text-[11px] font-semibold tracking-wider uppercase text-[#8A8A85]",
            tableRow: "border-b border-[#E8E8E4] hover:bg-[#F8F8F6] transition",
            modalBackdrop:
                "fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-3 sm:p-4 overflow-y-auto",
            modal: "relative w-full max-w-lg rounded-2xl border border-[#E8E8E4] bg-white overflow-hidden shadow-xl my-6",
            closeBtn:
                "w-8 h-8 rounded-full border border-[#DDDDD8] bg-[#F8F8F6] text-[#4A4A48] flex items-center justify-center hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition",
            balanceCard: "rounded-xl border border-[#DDDDD8] bg-white p-3.5",
            balanceIcon:
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#D4EBEF] bg-[#eef9fc] text-[#2fa9bf]",
            glowBar: "h-0.5 w-full bg-[#2fa9bf]",
            featureIcon:
                "w-5 h-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600",
            bonusChip:
                "inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 px-2.5 py-0.5 rounded-full text-xs",
            activeChip:
                "text-xs px-2.5 py-1 rounded-full border bg-emerald-50 border-emerald-200 text-emerald-700",
            inactiveChip:
                "text-xs px-2.5 py-1 rounded-full border bg-red-50 border-red-200 text-red-700",
            link: "inline-flex text-sm font-medium text-[#2fa9bf] hover:text-[#2296aa] hover:underline",
            loader: "animate-spin text-[#2fa9bf]",
            emptyIcon: "text-[#DDDDD8]",
            innerCard: "rounded-xl border border-[#E8E8E4] bg-[#F8F8F6]",
            accentInnerCard: "rounded-xl border border-[#D4EBEF] bg-[#eef9fc]",
            errorBox:
                "rounded-xl border border-red-200 bg-red-50 text-red-700",
            iconBtn:
                "w-10 h-10 rounded-xl border border-[#DDDDD8] bg-[#F8F8F6] text-[#4A4A48] flex items-center justify-center",
            pagination: "light" as const,
        };
    }

    return {
        page: "min-h-screen bg-[#080808] text-white p-4 sm:p-6",
        heading:
            "text-3xl sm:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent",
        subtitle: "text-[#8b8f8d] mt-2 text-sm sm:text-base",
        chip: "inline-flex items-center gap-1.5 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 px-3 py-1 rounded-full text-xs",
        card: "bg-[#101211] border border-[#1f2221] rounded-2xl",
        cardPopular:
            "bg-gradient-to-b from-[#101211] to-[#151716] border border-cyan-500/30 rounded-2xl shadow-[0_0_28px_rgba(6,182,212,0.12)]",
        muted: "text-[#8b8f8d]",
        body: "text-white",
        accent: "text-cyan-400",
        success: "text-green-400",
        warning: "text-amber-400",
        danger: "text-red-400",
        primaryBtn:
            "bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-90 text-white font-semibold rounded-xl transition shadow-md",
        secondaryBtn:
            "border border-[#2a2d2c] bg-[#080808] hover:bg-[#151716] text-white font-semibold rounded-xl transition",
        input: "w-full bg-[#080808] border border-[#1f2221] rounded-2xl px-4 py-3 outline-none text-white",
        select: "bg-[#101211] border border-[#1f2221] rounded-2xl py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500",
        tableWrap:
            "bg-[#101211] border border-[#1f2221] rounded-3xl overflow-hidden shadow-2xl",
        tableHead: "bg-[#080808] border-b border-[#1f2221]",
        tableHeadCell:
            "px-6 py-5 text-left text-[#b7bab8] font-semibold",
        tableRow: "border-b border-[#1f2221] hover:bg-[#151716] transition-all",
        modalBackdrop:
            "fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-4 overflow-y-auto",
        modal: "relative w-full max-w-lg rounded-2xl border border-cyan-500/20 bg-[#101211] overflow-hidden shadow-[0_0_40px_rgba(6,182,212,0.15)] my-6",
        closeBtn:
            "w-8 h-8 rounded-full bg-[#1a1d1c] border border-[#2a2d2c] flex items-center justify-center hover:bg-red-500/20 hover:border-red-500/30 transition-all",
        balanceCard: "rounded-xl border border-cyan-500/20 bg-[#101211] p-3.5",
        balanceIcon:
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-cyan-500/20 bg-cyan-500/10 text-cyan-400",
        glowBar: "h-0.5 w-full bg-gradient-to-r from-cyan-500 to-blue-600",
        featureIcon:
            "w-5 h-5 rounded-full bg-green-500/15 border border-green-500/20 flex items-center justify-center text-green-400",
        bonusChip:
            "inline-flex items-center gap-1.5 bg-green-500/10 border border-green-500/20 text-green-400 px-2.5 py-0.5 rounded-full text-xs",
        activeChip:
            "text-xs px-2.5 py-1 rounded-full border bg-green-500/10 border-green-500/20 text-green-400",
        inactiveChip:
            "text-xs px-2.5 py-1 rounded-full border bg-red-500/10 border-red-500/20 text-red-400",
        link: "inline-flex text-sm font-medium text-cyan-400 hover:underline",
        loader: "animate-spin text-cyan-400",
        emptyIcon: "text-[#3a3d3c]",
        innerCard: "rounded-xl border border-[#1f2221] bg-[#080808]",
        accentInnerCard: "rounded-xl border border-cyan-500/20 bg-cyan-500/5",
        errorBox:
            "rounded-xl border border-red-500/20 bg-red-500/10 text-red-400",
        iconBtn:
            "w-10 h-10 rounded-xl bg-[#1a1d1c] flex items-center justify-center",
        pagination: "dark" as const,
    };
};
